/*
 * The list page pattern: a filter form whose hidden PageNumber / SortOrder
 * fields are written by the header and pager controls, POSTed to the list
 * URL, with the response swapped into the table container. One instance per
 * list; the area exposes its methods as data-action handlers.
 *
 *   <form id="PersonFilterForm">  hidden PageNumber, SortOrder_0_FieldName,
 *                                 SortOrder_0_Ascending + the visible filters
 *   <div id="PersonListContainer"> table + pager (server partial)
 *
 * Header cells come from MyColumnHeader (data-sortcol / data-sorted /
 * data-sortasc); pager buttons carry data-page.
 */
import { ajaxSwap } from '../core/ajax';
import { one, type Target } from '../core/dom';
import { resetFields, serialize } from '../core/forms';

export interface ListOptions {
    /** Filter form id or selector. */
    form: string;
    /** Element that receives the list HTML. */
    container: string;
    /** List endpoint (relative to the app base). */
    url: string;
    method?: 'POST' | 'GET';
    busy?: Target;
    alertInto?: Target;
    alertTitle?: string;
    /** Hidden field ids, when a list names them differently. */
    pageField?: string;
    sortField?: string;
    sortAscendingField?: string;
    onApplied?: () => void;
}

function input(id: string): HTMLInputElement | null {
    return document.getElementById(id) as HTMLInputElement | null;
}

function setValue(id: string, value: string): void {
    const field = input(id);
    if (field) field.value = value;
}

export class ListController {
    private readonly options: Required<Pick<ListOptions, 'pageField' | 'sortField' | 'sortAscendingField' | 'method'>> & ListOptions;
    private enterBound = false;

    constructor(options: ListOptions) {
        this.options = {
            method: 'POST',
            pageField: 'PageNumber',
            sortField: 'SortOrder_0_FieldName',
            sortAscendingField: 'SortOrder_0_Ascending',
            ...options,
        };
    }

    get form(): HTMLFormElement | null {
        return one(this.options.form) as HTMLFormElement | null;
    }

    /** POST the filter form and swap the result into the container. */
    apply(): Promise<void> {
        const o = this.options;
        return ajaxSwap({
            container: o.container,
            url: o.url,
            method: o.method,
            data: serialize(o.form),
            busy: o.busy,
            alertInto: o.alertInto,
            alertTitle: o.alertTitle ?? 'Failed to apply filter',
            onSuccess: () => o.onApplied?.(),
        });
    }

    /** Back to page one with every visible filter reset; sort order is kept. */
    clear(): Promise<void> {
        resetFields(this.options.form);
        setValue(this.options.pageField, '1');
        return this.apply();
    }

    /** A sortable header was clicked: same column flips direction, a new column sorts ascending. */
    headerClick(header: Element): false {
        const column = header.getAttribute('data-sortcol');
        if (!column) return false;
        const sorted = header.getAttribute('data-sorted') === 'true';
        let ascending = header.getAttribute('data-sortasc') !== 'false';
        if (sorted) ascending = !ascending;
        setValue(this.options.sortField, column);
        setValue(this.options.sortAscendingField, ascending ? 'true' : 'false');
        setValue(this.options.pageField, '1');
        void this.apply();
        return false;
    }

    /** A pager button was clicked. */
    pagerApply(button: Element): void {
        const page = button.getAttribute('data-page');
        if (!page) return;
        setValue(this.options.pageField, page);
        void this.apply();
    }

    /** Checked row ids (checkboxes carrying data-id inside the container). */
    selectedIds(): number[] {
        const container = one(this.options.container);
        if (!container) return [];
        return Array.from(container.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-id]:checked'))
            .map(box => Number.parseInt(box.getAttribute('data-id') ?? '', 10))
            .filter(id => Number.isFinite(id) && id > 0);
    }

    /** The header "select all" box: mirror it onto every enabled row box. */
    toggleAll(master: Element): void {
        const container = one(this.options.container);
        if (!(master instanceof HTMLInputElement) || !container) return;
        for (const box of Array.from(container.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-id]'))) {
            if (!box.disabled) box.checked = master.checked;
        }
    }

    /** Enter in a text-like filter input applies the filter. Bound once, delegated, so re-rendered forms keep it. */
    bindEnter(): void {
        if (this.enterBound) return;
        this.enterBound = true;
        const formSelector = this.options.form.startsWith('#') || this.options.form.startsWith('.') ? this.options.form : `#${this.options.form}`;
        document.addEventListener('keydown', event => {
            if (event.key !== 'Enter') return;
            const target = event.target;
            if (!(target instanceof HTMLInputElement)) return;
            if (!target.closest(formSelector)) return;
            if (!['text', 'email', 'tel', 'search', 'number'].includes(target.type)) return;
            event.preventDefault();
            void this.apply();
        });
    }
}
